/**
 * 🎨 DECORATIVE BACKGROUND OBJECTS CONTROLLER
 * 
 * Change OBJECTS_THEME (1-10) to add subtle seasonal decorations to your backgrounds!
 * Objects appear BEHIND the UI content for an elegant, non-distracting experience.
 * 
 * Each theme has carefully positioned 2D objects that match seasonal events.
 */

// ============================================
// 👇 LEGACY FALLBACK - NOT USED ANYMORE
// Theme is now controlled by API (background.theme)
// This constant is only used as fallback in functions if API doesn't provide theme
// 0 = No objects (clean background)
// 1-10 = Seasonal themed objects
// ============================================
export const OBJECTS_THEME = 0; // Legacy fallback - API theme takes priority
// ============================================

/**
 * Object Configuration
 */
export interface BackgroundObject {
  id: string;
  type: string; // e.g., 'santa', 'reindeer', 'snowflake'
  emoji: string; // Emoji representation for the object
  position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  size: string; // CSS font-size or width
  opacity: number; // 0.0 - 1.0
  rotation?: number; // degrees
  zIndex: number; // Layering (1-9, always below UI content)
  animation?: string; // CSS animation class name
}

/**
 * Theme Configuration
 */
export interface ObjectsThemeConfig {
  id: number;
  name: string;
  description: string;
  season: string;
  objects: BackgroundObject[];
}

/**
 * 10 Seasonal Object Themes
 * Each theme has 3-5 objects strategically placed for elegance
 */
export const OBJECTS_THEMES: ObjectsThemeConfig[] = [
  {
    id: 0,
    name: 'No Objects',
    description: 'Clean background with no decorative objects',
    season: 'Year-round',
    objects: []
  },

  {
    id: 1,
    name: 'Christmas Magic',
    description: 'Santa, reindeer, and festive decorations',
    season: 'December 15-31',
    objects: [
      {
        id: 'santa-1',
        type: 'santa',
        emoji: '🎅',
        position: { bottom: '8%', left: '5%' },
        size: '120px',
        opacity: 0.32,
        rotation: -10,
        zIndex: 2
      },
      {
        id: 'reindeer-1',
        type: 'reindeer',
        emoji: '🦌',
        position: { top: '15%', right: '8%' },
        size: '80px',
        opacity: 0.29,
        rotation: 15,
        zIndex: 1
      },
      {
        id: 'gift-1',
        type: 'gift',
        emoji: '🎁',
        position: { bottom: '25%', right: '12%' },
        size: '60px',
        opacity: 0.35,
        rotation: -8,
        zIndex: 3
      },
      {
        id: 'tree-1',
        type: 'tree',
        emoji: '🎄',
        position: { top: '40%', left: '3%' },
        size: '90px',
        opacity: 0.31,
        rotation: 0,
        zIndex: 2
      }
    ]
  },

  {
    id: 2,
    name: 'New Year Celebration',
    description: 'Champagne, fireworks, and party vibes',
    season: 'December 26 - January 7',
    objects: [
      {
        id: 'champagne-1',
        type: 'champagne',
        emoji: '🍾',
        position: { bottom: '12%', right: '6%' },
        size: '100px',
        opacity: 0.33,
        rotation: -20,
        zIndex: 3
      },
      {
        id: 'fireworks-1',
        type: 'fireworks',
        emoji: '🎆',
        position: { top: '10%', left: '8%' },
        size: '90px',
        opacity: 0.31,
        rotation: 0,
        zIndex: 2
      },
      {
        id: 'confetti-1',
        type: 'confetti',
        emoji: '🎉',
        position: { top: '30%', right: '10%' },
        size: '70px',
        opacity: 0.29,
        rotation: 25,
        zIndex: 1
      },
      {
        id: 'clock-1',
        type: 'clock',
        emoji: '🕛',
        position: { bottom: '20%', left: '4%' },
        size: '85px',
        opacity: 0.3,
        rotation: 0,
        zIndex: 2
      }
    ]
  },

  {
    id: 3,
    name: 'Valentine Romance',
    description: 'Hearts, roses, and love in the air',
    season: 'February 1-14',
    objects: [
      {
        id: 'heart-1',
        type: 'heart',
        emoji: '💝',
        position: { top: '12%', right: '7%' },
        size: '95px',
        opacity: 0.32,
        rotation: -12,
        zIndex: 2
      },
      {
        id: 'rose-1',
        type: 'rose',
        emoji: '🌹',
        position: { bottom: '15%', left: '6%' },
        size: '80px',
        opacity: 0.31,
        rotation: 18,
        zIndex: 3
      },
      {
        id: 'cupid-1',
        type: 'cupid',
        emoji: '💘',
        position: { top: '35%', left: '4%' },
        size: '70px',
        opacity: 0.28,
        rotation: 0,
        zIndex: 1
      },
      {
        id: 'heart-2',
        type: 'heart',
        emoji: '💕',
        position: { bottom: '30%', right: '8%' },
        size: '65px',
        opacity: 0.3,
        rotation: 15,
        zIndex: 2
      }
    ]
  },

  {
    id: 4,
    name: 'St Patrick Luck',
    description: 'Shamrocks, gold coins, and Irish charm',
    season: 'March 10-17',
    objects: [
      {
        id: 'shamrock-1',
        type: 'shamrock',
        emoji: '☘️',
        position: { top: '18%', left: '5%' },
        size: '90px',
        opacity: 0.33,
        rotation: -15,
        zIndex: 2
      },
      {
        id: 'coins-1',
        type: 'coins',
        emoji: '🪙',
        position: { bottom: '12%', right: '7%' },
        size: '85px',
        opacity: 0.31,
        rotation: 10,
        zIndex: 3
      },
      {
        id: 'hat-1',
        type: 'hat',
        emoji: '🎩',
        position: { top: '40%', right: '9%' },
        size: '75px',
        opacity: 0.29,
        rotation: -20,
        zIndex: 1
      },
      {
        id: 'clover-1',
        type: 'clover',
        emoji: '🍀',
        position: { bottom: '28%', left: '4%' },
        size: '70px',
        opacity: 0.3,
        rotation: 25,
        zIndex: 2
      }
    ]
  },

  {
    id: 5,
    name: 'Spring Bloom',
    description: 'Flowers, butterflies, and spring awakening',
    season: 'March 20 - May 31',
    objects: [
      {
        id: 'flower-1',
        type: 'flower',
        emoji: '🌸',
        position: { top: '15%', right: '6%' },
        size: '95px',
        opacity: 0.32,
        rotation: -10,
        zIndex: 2
      },
      {
        id: 'butterfly-1',
        type: 'butterfly',
        emoji: '🦋',
        position: { top: '35%', left: '5%' },
        size: '70px',
        opacity: 0.29,
        rotation: 20,
        zIndex: 3
      },
      {
        id: 'bunny-1',
        type: 'bunny',
        emoji: '🐰',
        position: { bottom: '18%', right: '8%' },
        size: '85px',
        opacity: 0.31,
        rotation: -8,
        zIndex: 2
      },
      {
        id: 'tulip-1',
        type: 'tulip',
        emoji: '🌷',
        position: { bottom: '32%', left: '4%' },
        size: '75px',
        opacity: 0.3,
        rotation: 12,
        zIndex: 1
      }
    ]
  },

  {
    id: 6,
    name: 'Summer Vibes',
    description: 'Beach, sun, and tropical paradise',
    season: 'June 1 - August 31',
    objects: [
      {
        id: 'sun-1',
        type: 'sun',
        emoji: '☀️',
        position: { top: '10%', right: '8%' },
        size: '100px',
        opacity: 0.3,
        rotation: 0,
        zIndex: 1
      },
      {
        id: 'beach-1',
        type: 'beach',
        emoji: '🏖️',
        position: { bottom: '15%', left: '5%' },
        size: '90px',
        opacity: 0.32,
        rotation: -5,
        zIndex: 2
      },
      {
        id: 'cocktail-1',
        type: 'cocktail',
        emoji: '🍹',
        position: { top: '40%', left: '4%' },
        size: '75px',
        opacity: 0.31,
        rotation: 15,
        zIndex: 3
      },
      {
        id: 'palm-1',
        type: 'palm',
        emoji: '🌴',
        position: { bottom: '25%', right: '7%' },
        size: '95px',
        opacity: 0.29,
        rotation: -10,
        zIndex: 2
      }
    ]
  },

  {
    id: 7,
    name: 'Independence Day',
    description: 'American flag, fireworks, and patriotic spirit',
    season: 'June 28 - July 7',
    objects: [
      {
        id: 'flag-1',
        type: 'flag',
        emoji: '🇺🇸',
        position: { top: '12%', left: '6%' },
        size: '100px',
        opacity: 0.33,
        rotation: -8,
        zIndex: 2
      },
      {
        id: 'fireworks-2',
        type: 'fireworks',
        emoji: '🎇',
        position: { top: '30%', right: '7%' },
        size: '85px',
        opacity: 0.3,
        rotation: 0,
        zIndex: 3
      },
      {
        id: 'star-1',
        type: 'star',
        emoji: '⭐',
        position: { bottom: '20%', left: '5%' },
        size: '70px',
        opacity: 0.31,
        rotation: 20,
        zIndex: 1
      },
      {
        id: 'sparkler-1',
        type: 'sparkler',
        emoji: '🎆',
        position: { bottom: '35%', right: '8%' },
        size: '80px',
        opacity: 0.29,
        rotation: -15,
        zIndex: 2
      }
    ]
  },

  {
    id: 8,
    name: 'Halloween Spooky',
    description: 'Pumpkins, ghosts, and spooky fun',
    season: 'October 15-31',
    objects: [
      {
        id: 'pumpkin-1',
        type: 'pumpkin',
        emoji: '🎃',
        position: { bottom: '12%', left: '6%' },
        size: '100px',
        opacity: 0.34,
        rotation: -10,
        zIndex: 3
      },
      {
        id: 'ghost-1',
        type: 'ghost',
        emoji: '👻',
        position: { top: '15%', right: '7%' },
        size: '85px',
        opacity: 0.3,
        rotation: 8,
        zIndex: 2
      },
      {
        id: 'bat-1',
        type: 'bat',
        emoji: '🦇',
        position: { top: '38%', left: '4%' },
        size: '70px',
        opacity: 0.28,
        rotation: -20,
        zIndex: 1
      },
      {
        id: 'witch-1',
        type: 'witch',
        emoji: '🧙',
        position: { bottom: '28%', right: '9%' },
        size: '90px',
        opacity: 0.31,
        rotation: 12,
        zIndex: 2
      }
    ]
  },

  {
    id: 9,
    name: 'Thanksgiving Harvest',
    description: 'Autumn leaves, pumpkins, and harvest bounty',
    season: 'November 15-28',
    objects: [
      {
        id: 'turkey-1',
        type: 'turkey',
        emoji: '🦃',
        position: { bottom: '15%', right: '7%' },
        size: '95px',
        opacity: 0.32,
        rotation: -12,
        zIndex: 3
      },
      {
        id: 'pumpkin-2',
        type: 'pumpkin',
        emoji: '🎃',
        position: { bottom: '30%', left: '5%' },
        size: '85px',
        opacity: 0.31,
        rotation: 10,
        zIndex: 2
      },
      {
        id: 'leaves-1',
        type: 'leaves',
        emoji: '🍂',
        position: { top: '18%', right: '8%' },
        size: '75px',
        opacity: 0.29,
        rotation: 25,
        zIndex: 1
      },
      {
        id: 'corn-1',
        type: 'corn',
        emoji: '🌽',
        position: { top: '40%', left: '4%' },
        size: '70px',
        opacity: 0.3,
        rotation: -8,
        zIndex: 2
      }
    ]
  },

  {
    id: 10,
    name: 'Winter Wonderland',
    description: 'Snowman, snowflakes, and cozy winter',
    season: 'December 1-14, January 8 - February 28',
    objects: [
      {
        id: 'snowman-1',
        type: 'snowman',
        emoji: '⛄',
        position: { bottom: '10%', left: '6%' },
        size: '100px',
        opacity: 0.33,
        rotation: 0,
        zIndex: 3
      },
      {
        id: 'snowflake-1',
        type: 'snowflake',
        emoji: '❄️',
        position: { top: '12%', right: '8%' },
        size: '80px',
        opacity: 0.3,
        rotation: 0,
        zIndex: 1
      },
      {
        id: 'pine-1',
        type: 'pine',
        emoji: '🌲',
        position: { top: '35%', left: '4%' },
        size: '90px',
        opacity: 0.31,
        rotation: -5,
        zIndex: 2
      },
      {
        id: 'mitten-1',
        type: 'mitten',
        emoji: '🧤',
        position: { bottom: '25%', right: '7%' },
        size: '70px',
        opacity: 0.29,
        rotation: 18,
        zIndex: 2
      }
    ]
  }
];

/**
 * Get the currently selected objects theme
 * @param themeId - Optional theme ID from API. If not provided, uses OBJECTS_THEME constant
 */
export const getSelectedObjectsTheme = (themeId?: number): ObjectsThemeConfig => {
  const themeToUse = themeId !== undefined ? themeId : OBJECTS_THEME;
  const theme = OBJECTS_THEMES.find(t => t.id === themeToUse);

  if (!theme) {
    console.warn(`⚠️ Invalid theme: ${themeToUse}. Using theme 0 (no objects).`);
    return OBJECTS_THEMES[0]; // Default to no objects
  }

  return theme;
};

/**
 * Check if objects should be displayed
 * @param themeId - Optional theme ID from API. If not provided, uses OBJECTS_THEME constant
 */
export const shouldShowObjects = (themeId?: number): boolean => {
  const themeToUse = themeId !== undefined ? themeId : OBJECTS_THEME;
  return themeToUse > 0;
};

/**
 * Get objects for current theme
 * @param themeId - Optional theme ID from API. If not provided, uses OBJECTS_THEME constant
 */
export const getBackgroundObjects = (themeId?: number): BackgroundObject[] => {
  return getSelectedObjectsTheme(themeId).objects;
};

/**
 * Log current objects theme to console
 */
export const logCurrentObjectsTheme = (): void => {
  const theme = getSelectedObjectsTheme();
  console.log(`
🎨 Decorative Objects Theme: ${theme.name} (${OBJECTS_THEME}/10)
📝 ${theme.description}
🗓️  Season: ${theme.season}
🎯 Objects: ${theme.objects.length} items
  `);
};

// Auto-log theme on import (only in development)
if (process.env.NODE_ENV === 'development') {
  logCurrentObjectsTheme();
}

export default {
  OBJECTS_THEME,
  OBJECTS_THEMES,
  getSelectedObjectsTheme,
  shouldShowObjects,
  getBackgroundObjects,
  logCurrentObjectsTheme
};

